import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import sidebarStyle from "../../style/components/bars/SideBar.module.css";
import { IoChatbubblesOutline } from "react-icons/io5";
import { CiHome } from "react-icons/ci";
import { CiBellOn } from "react-icons/ci";
import { IoIosMore } from "react-icons/io";
import WorkSpace from "./WorkSpace";
import {
  getSelectedCompany,
  getUserDetails,
  getUserToken,
} from "../../utils/auth";
import ProfileDropdown from "../UserToggle/ProfileToggle";
import Profile from "../../Pages/profile/Profile";
import ChangePassword from "../../Pages/auth/ChangePassword";
import FallbackAvatar from "../ui/FallbackAvatar/FallbackAvatar";
import { useLoginUser } from "../../hooks/useCommonHooks";
import AccountDeactive from "../ui/Warning/AccountDeactive";
import WebSocketOnline from "../../services/onlineSocketService";

const SideBar = ({ onMenuSelect, onWorkspaceUpdate = () => {} }) => {
  const location = useLocation();
  const selectedCompany = getSelectedCompany() || {};
  const { userData: loginData, refreshLoginData } = useLoginUser();
  const [showPopup, setShowPopup] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");

  const activeMenu = (item) => {
    if (item.name === "More") {
      const morePaths = [
        "/accountsummary",
        "/userlist",
        "/userlist/add",
        "/companylist",
        "/companylist/add",
        "/group",
        "/group/add",
        "/channel",
        "/channel/add",
        "/userandrole",
        "/userandrole/add",
        "/logs",
        "/privacy-policy",
        "/aboutus",
      ];
      return morePaths.some((p) => location.pathname.startsWith(p));
    }
    return location.pathname.startsWith(item.path);
  };

  const [isWorkSpaceOpen, setWorkSpaceOpen] = useState(false);
  const [isProfileOpen, setProfileOpen] = useState(false);
  const [isChngPaswOpen, setChngPaswOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const token = getUserToken();

    if (!token) {
      console.error("No authentication token found");
      return;
    }

    // Set up WebSocket callbacks
    WebSocketOnline.setCallback("onconnect", () => {
      console.log("WebSocket connected");
      setConnectionStatus("connected");
    });

    WebSocketOnline.setCallback("onreconnect", () => {
      console.log("WebSocket reconnected");
      setConnectionStatus("connected");
    });

    WebSocketOnline.setCallback("ondisconnect", () => {
      console.log("WebSocket disconnected");
      setConnectionStatus("disconnected");
    });

    WebSocketOnline.setCallback("onerror", (error) => {
      console.error("WebSocket error:", error);
      setConnectionStatus("error");
    });

    // Connect to WebSocket
    WebSocketOnline.connect(token);

    // Cleanup function
    return () => {
      WebSocketOnline.removeCallback("onconnect");
      WebSocketOnline.removeCallback("onreconnect");
      WebSocketOnline.removeCallback("ondisconnect");
      WebSocketOnline.removeCallback("onerror");

      // Don't disconnect here as we want the WebSocket to persist
      // across navigation, but we'll handle page unload separately
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      // Reconnect WebSocket when window gains focus if not connected
      const token = getUserToken();
      if (token && connectionStatus !== "connected") {
        console.log("Window focused - reconnecting WebSocket");
        WebSocketOnline.connect(token);
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [connectionStatus]);

  const userDetails = getUserDetails();
  const menuItems = [
    { icon: <CiHome size={20} />, name: "Home", path: "/homepage" },
    { icon: <IoChatbubblesOutline size={20} />, name: "DMS", path: "/dms" },
    { icon: <CiBellOn size={20} />, name: "Activity", path: "/activity" },
    { icon: <IoIosMore size={20} />, name: "More", path: "/accountsummary" },
  ];
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [isOpen, setIsOpen] = useState(false);

  const handleMenuClick = (item) => {
    if (item.action) item.action();
    if (onMenuSelect) onMenuSelect(item.name);
  };

  const openWorkSpace = () => {
    setWorkSpaceOpen(true);
  };

  const closeWorkSpace = () => {
    setWorkSpaceOpen(false);
  };

  const companyData = (data) => {
    if (data) {
      onWorkspaceUpdate(data);
    }
  };

  const openToggle = () => {
    setIsOpen(true);
  };

  const closeToggle = () => {
    setIsOpen(false);
  };

  const handleClick = (data) => {
    if (data === "Profile") {
      setProfileOpen(true);
    } else {
      setChngPaswOpen(true);
    }
  };
  useEffect(() => {
    if (loginData?.employee_details?.active === "1") {
      setShowPopup(true);
    }
  }, [loginData]);

  const closePopup = () => setShowPopup(false);

  const handleUpdate = () => {
    refreshLoginData();
  };

  return (
    <>
      <div className={sidebarStyle.sidebarContainer}>
        <div className={sidebarStyle.sidebarContainerChild}>
          <div className={sidebarStyle.sidebarHeader}>
            <div
              className={sidebarStyle.sidebarCompanyLogo}
              onClick={openWorkSpace}
            >
              <img
                src={`${BASE_URL}${selectedCompany?.company_logo}`}
                alt="Company Logo"
              />
            </div>
          </div>

          <nav className={sidebarStyle.sidebarMenus}>
            <ul>
              {menuItems.map((item) => (
                <li key={item.name} onClick={() => handleMenuClick(item)}>
                  <Link
                    to={item.path}
                    className={`${sidebarStyle.sidebarMenuList} ${
                      activeMenu(item) ? sidebarStyle.activeMenu : ""
                    }`}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className={sidebarStyle.sidebarBottom}>
            <div className={sidebarStyle.sideBaruser} onClick={openToggle}>
              {imgError || !userDetails?.emp_pic ? (
                <FallbackAvatar name={userDetails?.name || "User"} size={40} />
              ) : (
                <img
                  src={`${BASE_URL}${userDetails?.emp_pic}`}
                  alt="User Logo"
                  onError={() => setImgError(true)}
                />
              )}
              <span
                className={`${sidebarStyle.OnlineIndicator} ${
                  connectionStatus === "connected"
                    ? sidebarStyle.online
                    : sidebarStyle.offline
                }`}
                title={connectionStatus}
              ></span>{" "}
            </div>
          </div>
        </div>
      </div>
      <WorkSpace
        show={isWorkSpaceOpen}
        close={closeWorkSpace}
        companyUpdated={companyData}
      />
      {isChngPaswOpen && (
        <ChangePassword OnClose={() => setChngPaswOpen(false)} />
      )}

      <ProfileDropdown show={isOpen} close={closeToggle} Click={handleClick} />
      {isProfileOpen && (
        <Profile onClose={() => setProfileOpen(false)} upDated={handleUpdate} />
      )}
      {showPopup && <AccountDeactive onClose={closePopup} />}
    </>
  );
};

export default SideBar;
